import i18n from 'i18next';
import {
  initReactI18next,
  useTranslation as useVendorTranslation,
} from 'react-i18next';

import {
  CsvUploadDataType,
  CsvUploadStatus,
} from '@payaca/types/csvUploadTypes';
import { DeepPartial } from '@payaca/utilities/types';

/** UK English */
const defaultLanguage = 'en-GB';

type DefaultLanguage = typeof defaultLanguage;

const routes = {
  dashboard: '/dashboard',
  deals: '/deals',
  deal: '/projects/:projectId/:tab?',
  customers: '/customers',
  customer: '/customers/:customerId',
  schedule: '/schedule',
  dispatch: '/dispatch',
  tasks: '/tasks',
  items: '/items',
  item: '/items/:itemId',
  invoices: '/invoices',
  materials: '/materials',
  material: '/materials/:materialId',
  suppliers: '/suppliers',
  supplier: '/suppliers/:supplierId',
  automations: '/automations',
  automation: '/automations/:automationId',
  analytics: '/analytics',
  profile: '/profile',
  myCompany: '/my-company',
  users: '/users',
  userProfile: '/users/:userId',
  manageSubscription: '/manage-subscription',
  csvUploads: '/csv-uploads',
  tags: '/tags',
  connections: '/connections',
  logOut: '/logout',
  servicePlans: '/service-plans',
  servicePlanCustomerReminders: '/service-plans/customer-reminders',
  servicePlan: '/service-plans/service-plan/:servicePlanId',
  templates: '/templates',
  timelogs: '/timelogs',
} as const;

type Routes = typeof routes;

type PersistentRoutes = Pick<
  Routes,
  | 'dashboard'
  | 'deals'
  | 'invoices'
  | 'customers'
  | 'schedule'
  | 'dispatch'
  | 'tasks'
  | 'timelogs'
  | 'items'
  | 'materials'
  | 'suppliers'
  | 'automations'
  | 'analytics'
  | 'servicePlans'
>;

type AccountRoutes = Pick<
  Routes,
  | 'profile'
  | 'myCompany'
  | 'users'
  | 'manageSubscription'
  | 'csvUploads'
  | 'tags'
  | 'connections'
  | 'logOut'
  | 'templates'
>;

type LabelWithTooltip = {
  label: string;
  tooltip?: string | null;
};

type Cta = LabelWithTooltip;

type Page = {
  title: string;
  description?: string | null;
  heading?: string | null;
  ctas?: Record<string, Cta> | null;
};

type PersistentNavItems = Record<keyof PersistentRoutes, LabelWithTooltip>;
type AccountNavItems = Record<keyof AccountRoutes, LabelWithTooltip>;

type Pages = Record<keyof Routes, Page>;

type AutomationsTranslation = {
  entityTypes: Record<string, string>;
  triggerEvents: Record<string, string>;
  conditions: {
    typeNames: Record<string, string>;
    boolean: Record<string, string>;
    labels: Record<string, string>;
    operators: Record<string, string>;
    statuses: Record<string, string>;
    dayOfWeek: Record<string, string>;
  };
  actions: {
    labels: Record<string, string>;
    sendToEmail: Record<string, string>;
    sendToNumber: Record<string, string>;
  };
};

type PipelineGuardConditionTranslation = {
  mustBe?: string;
  mustBe_blank?: string;
  mustNotBe?: string;
  mustNotBe_blank?: string;
  hasBeen?: string;
  hasBeen_blank?: string;
  hasNotBeen?: string;
  hasNotBeen_blank?: string;
  // Extension for the customFieldCondition guard. Existing simple/tag
  // conditions only set the `mustBe` / `hasBeen` family above; the
  // customFieldCondition entry uses these richer keys instead, since each
  // operator carries its own UI scaffolding (field picker, operator
  // dropdown, optional comparison-value input) rather than fitting the
  // single-string mustBe/hasBeen mould.
  entry?: string;
  /** Shorter copy shown when the option is the active selection. */
  selectedLabel?: string;
  field?: { label: string; placeholder: string };
  operator?: {
    label: string;
    placeholder: string;
    equals: string;
    notEquals: string;
    isSet: string;
    isNotSet: string;
    allChildrenAreSet: string;
    allChildrenAreNotSet: string;
    anyChildIsSet: string;
  };
  comparisonValue?: { label: string; placeholder: string };
  /** Compact, value-substituted templates rendered in the guard-result UI. */
  summary?: {
    equals: string;
    notEquals: string;
    isSet: string;
    isNotSet: string;
    allChildrenAreSet: string;
    allChildrenAreNotSet: string;
    anyChildIsSet: string;
  };
};

type PipelineGuardsTranslation = {
  conditions: Record<string, PipelineGuardConditionTranslation>;
};

type DashboardMetric = {
  up: string;
  down: string;
  from: string;
  overdue: string;
  allClear: string;
};

type DashboardDateRange = {
  label: string;
  thisMonth: string;
  lastMonth: string;
  last3Months: string;
  last6Months: string;
  last12Months: string;
  allTime: string;
  previousThisMonth: string;
  previousLastMonth: string;
  previous3Months: string;
  previous6Months: string;
  previous12Months: string;
};

type DashboardSummary = {
  title: string;
  comparison: string;
  comparisonPrefix: string;
  revenueSecured: string;
  revenueSecuredTooltip: string;
  revenueSecuredEmpty: string;
  revenueReceived: string;
  revenueReceivedTooltip: string;
  revenueReceivedEmpty: string;
  overdueTitle: string;
  overdueTooltip: string;
};

type DashboardProposals = {
  title: string;
  titleTooltip: string;
  sent: string;
  sentTooltip: string;
  accepted: string;
  acceptedTooltip: string;
  declined: string;
  declinedTooltip: string;
  acceptanceRate: string;
  xAxisTitle: string;
  empty: string;
};

type DashboardInvoices = {
  title: string;
  sent: string;
  sentTooltip: string;
  paid: string;
  paidTooltip: string;
  overdue: string;
  overdueTooltip: string;
  paidRate: string;
  xAxisTitle: string;
  empty: string;
};

type DashboardErrors = {
  summaryError: string;
  proposalsError: string;
  invoicesError: string;
  breakdownError: string;
};

type DashboardQuickStats = {
  proposalsSent: string;
  proposalsSentTooltip: string;
  pendingProposals: string;
  pendingProposalsTooltip: string;
  invoicesSent: string;
  awaitingPayment: string;
  collectionRate: string;
  ofInvoicedValue: string;
  avgDealSize: string;
  avgDealSizeTooltip: string;
  invoice: string;
};

type DashboardBreakdown = {
  proposalStatus: string;
  proposalStatusTooltip: string;
  invoiceStatus: string;
  pending: string;
  accepted: string;
  declined: string;
  paid: string;
  awaiting: string;
  overdue: string;
  noProposals: string;
  noInvoices: string;
};

type DashboardTranslation = {
  header: string;
  greeting: string;
  trends: string;
  metric: DashboardMetric;
  dateRange: DashboardDateRange;
  summary: DashboardSummary;
  proposals: DashboardProposals;
  invoices: DashboardInvoices;
  errors: DashboardErrors;
  quickStats: DashboardQuickStats;
  breakdown: DashboardBreakdown;
};

type WebappTranslation = {
  navigation: {
    persistent: PersistentNavItems;
    account: AccountNavItems;
  };
  pages: Pages;
  csvUploads: {
    dataTypes: Record<CsvUploadDataType, string>;
    statuses: Record<CsvUploadStatus, string>;
  };
  automations: AutomationsTranslation;
  pipelineGuards: PipelineGuardsTranslation;
  dashboard: DashboardTranslation;
};

const supportedLanguages = [
  defaultLanguage,
  'en-US', // US English
  // TODO: Uncomment this once translations have been approved
  // 'af-ZA', // Afrikaans (South Africa)
] as const;

type SupportedLanguages = (typeof supportedLanguages)[number];
type AdditionalLanguages = Exclude<SupportedLanguages, DefaultLanguage>;

const translations: Record<DefaultLanguage, WebappTranslation> &
  Record<AdditionalLanguages, DeepPartial<WebappTranslation>> &
  Record<string, DeepPartial<WebappTranslation>> = {
  'en-GB': {
    navigation: {
      persistent: {
        dashboard: {
          label: 'Dashboard',
        },
        deals: {
          label: 'Projects',
        },
        customers: {
          label: 'Customers',
        },
        schedule: {
          label: 'Schedule',
        },
        dispatch: {
          label: 'Dispatch',
        },
        tasks: {
          label: 'Tasks',
        },
        timelogs: {
          label: 'Timelogs',
        },
        items: {
          label: 'Items you sell',
        },
        invoices: {
          label: 'Invoices',
        },
        materials: {
          label: 'Materials',
        },
        suppliers: {
          label: 'Suppliers',
        },
        automations: {
          label: 'Automations',
        },
        analytics: {
          label: 'Analytics',
        },
        servicePlans: {
          label: 'Service Plans',
        },
      },
      account: {
        profile: {
          label: 'Profile',
        },
        myCompany: {
          label: 'My company',
        },
        users: {
          label: 'Users',
        },
        manageSubscription: {
          label: 'Manage subscription',
        },
        tags: {
          label: 'Tags',
        },
        csvUploads: {
          label: 'Import data',
        },
        connections: {
          label: 'Connections',
        },
        logOut: {
          label: 'Log out',
        },
        templates: {
          label: 'Templates',
        },
      },
    },
    pages: {
      timelogs: {
        title: 'Timelog Reporting',
      },
      dashboard: {
        title: 'Dashboard',
      },
      invoices: {
        title: 'Invoices',
      },
      deals: {
        title: 'Projects',
        ctas: {
          createDeal: {
            label: 'New Project',
          },
        },
      },
      deal: {
        title: 'Project: {{reference}}',
      },
      customers: {
        title: 'Customers',
        heading: 'Customers',
        ctas: {
          createCustomer: {
            label: 'Create new',
          },
        },
      },
      customer: {
        title: 'Customer: {{name}}',
      },
      schedule: {
        title: 'Schedule',
        heading: 'Schedule',
      },
      dispatch: {
        title: 'Dispatch',
        heading: 'Dispatch',
      },
      tasks: {
        title: 'Tasks',
        heading: 'Tasks',
        ctas: {
          createTask: {
            label: 'Create new',
          },
        },
      },
      items: {
        title: 'Items you sell',
      },
      item: {
        title: 'Item: {{reference}}',
      },
      materials: {
        title: 'Materials',
      },
      material: {
        title: 'Material: {{name}}',
      },
      suppliers: {
        title: 'Suppliers',
      },
      supplier: {
        title: 'Supplier: {{name}}',
      },
      automations: {
        title: 'Automations',
      },
      automation: {
        title: 'Automation: {{name}}',
      },
      analytics: {
        title: 'Analytics',
      },
      profile: {
        title: 'Profile',
      },
      myCompany: {
        title: 'My company',
      },
      users: {
        title: 'Users',
      },
      userProfile: {
        title: 'User: {{name}}',
      },
      manageSubscription: {
        title: 'Manage subscription',
      },
      tags: {
        title: 'Tags',
      },
      csvUploads: {
        title: 'Import data',
      },
      connections: {
        title: 'Connections',
      },
      logOut: {
        title: 'Log out',
      },
      servicePlans: {
        title: 'Service Plans',
      },
      servicePlan: {
        title: 'Service Plan: {{name}}',
      },
      servicePlanCustomerReminders: {
        title: 'Customer Reminders',
      },
      templates: {
        title: 'Templates',
      },
    },
    csvUploads: {
      dataTypes: {
        customers: 'Customers',
        deals: 'Projects',
        lineItems: 'Line Items',
        materials: 'Materials',
        serviceReminders: 'Service Reminders',
        supplierMaterials: 'Materials',
        suppliers: 'Suppliers',
      } as any,
      statuses: {
        created: 'Created',
        uploading: 'Uploading',
        uploadAborted: 'Upload aborted',
        uploadFailed: 'Upload failed',
        uploadComplete: 'Uploaded',
        received: 'Received',
        validating: 'Validating',
        validationFailed: 'Validation failed',
        validationComplete: 'Validation complete',
        pendingConfirmation: 'Pending confirmation',
        processing: 'Processing',
        processingFailed: 'Processing failed',
        complete: 'Complete',
      },
    },
    automations: {
      entityTypes: {
        CHANGE_PROPOSAL: 'Change proposal',
        DNO_APPLICATION: 'DNO application',
        ESTIMATE: 'Estimate',
        EVENT: 'Event',
        INVOICE: 'Invoice',
        PROJECT: 'Project',
        PROPOSAL: 'Proposal',
        QUOTE: 'Quote',
      },
      triggerEvents: {
        // State changes
        CREATED: '{{entity}} is created',
        ACCEPTED: '{{entity}} is accepted',
        DECLINED: '{{entity}} is declined',
        CANCELLED: '{{entity}} is cancelled',
        SENT: '{{entity}} is sent',
        EXPIRED: '{{entity}} has expired',
        VOIDED: '{{entity}} is voided',
        FULLY_PAID: '{{entity}} is fully paid',
        ASSIGNED: '{{entity}} is assigned',
        TAGGED: '{{entity}} is tagged',
        ACCEPTANCE_REMOVED: '{{entity}} acceptance removed',
        PAYMENT_RECEIVED: '{{entity}} payment received',
        // Pipeline/Task
        ADDED_TO_PIPELINE: '{{entity}} added to pipeline',
        PIPELINE_STAGE_CHANGED: '{{entity}} pipeline stage changed',
        TASK_STATUS_CHANGED: '{{entity}} task status changed',
        EVENT_TASK_STATUS_CHANGED: '{{entity}} task status changed',
        CONNECTION_APPLICATION_STATUS_CHANGED:
          '{{entity}} connection status changed',
        PIPELINE_STAGE_CHANGED_YESTERDAY: '{{entity}} stage changed yesterday',
        PIPELINE_STAGE_CHANGED_2_DAYS_AGO:
          '{{entity}} stage changed 2 days ago',
        PIPELINE_STAGE_CHANGED_3_DAYS_AGO:
          '{{entity}} stage changed 3 days ago',
        PIPELINE_STAGE_CHANGED_4_DAYS_AGO:
          '{{entity}} stage changed 4 days ago',
        PIPELINE_STAGE_CHANGED_5_DAYS_AGO:
          '{{entity}} stage changed 5 days ago',
        PIPELINE_STAGE_CHANGED_6_DAYS_AGO:
          '{{entity}} stage changed 6 days ago',
        PIPELINE_STAGE_CHANGED_7_DAYS_AGO:
          '{{entity}} stage changed 7 days ago',
        // Scheduled runs
        SCHEDULED_DAILY_RUN: 'Scheduled daily run for {{entity}}',
        REALTIME_RUN: 'Realtime run for {{entity}}',
        // Due dates
        DUE_IN_7_DAYS: '{{entity}} due in 7 days',
        DUE_IN_3_DAYS: '{{entity}} due in 3 days',
        DUE_IN_1_DAY: '{{entity}} due in 1 day',
        DUE_TODAY: '{{entity}} due today',
        DUE_YESTERDAY: '{{entity}} was due yesterday',
        DUE_3_DAYS_AGO: '{{entity}} was due 3 days ago',
        DUE_7_DAYS_AGO: '{{entity}} was due 7 days ago',
        // Expiry dates
        EXPIRES_IN_28_DAYS: '{{entity}} expires in 28 days',
        EXPIRES_IN_21_DAYS: '{{entity}} expires in 21 days',
        EXPIRES_IN_14_DAYS: '{{entity}} expires in 14 days',
        EXPIRES_IN_7_DAYS: '{{entity}} expires in 7 days',
        EXPIRES_IN_3_DAYS: '{{entity}} expires in 3 days',
        EXPIRES_IN_1_DAY: '{{entity}} expires in 1 day',
        EXPIRES_TODAY: '{{entity}} expires today',
        EXPIRED_YESTERDAY: '{{entity}} expired yesterday',
        EXPIRED_3_DAYS_AGO: '{{entity}} expired 3 days ago',
        EXPIRED_7_DAYS_AGO: '{{entity}} expired 7 days ago',
        // Event start times
        STARTS_IN_1_MONTH: '{{entity}} starts in 1 month',
        STARTS_IN_21_DAYS: '{{entity}} starts in 21 days',
        STARTS_IN_14_DAYS: '{{entity}} starts in 14 days',
        STARTS_IN_7_DAYS: '{{entity}} starts in 7 days',
        STARTS_IN_3_DAYS: '{{entity}} starts in 3 days',
        STARTS_IN_1_DAY: '{{entity}} starts in 1 day',
        STARTS_IN_1_HOUR: '{{entity}} starts in 1 hour',
        STARTS_NOW: '{{entity}} starts now',
        STARTS_TODAY: '{{entity}} starts today',
        // Event end times
        FINISHES_NOW: '{{entity}} finishes now',
        FINISHES_TODAY: '{{entity}} finishes today',
        FINISHED_YESTERDAY: '{{entity}} finished yesterday',
        FINISHED_3_DAYS_AGO: '{{entity}} finished 3 days ago',
        FINISHED_7_DAYS_AGO: '{{entity}} finished 7 days ago',
        FINISHED_1_MONTH_AGO: '{{entity}} finished 1 month ago',
        FINISHED_11_MONTHS_AGO: '{{entity}} finished 11 months ago',
        FINISHED_1_YEAR_AGO: '{{entity}} finished 1 year ago',
      },
      conditions: {
        typeNames: {
          AssigneesPhoneCondition: 'Assignee phone',
          ConnectionApplicationStatusCondition: 'Connection application status',
          CustomerEmailCondition: 'Customer email',
          CustomerNameCondition: 'Customer name',
          CustomerPhoneCondition: 'Customer phone',
          EventAssigneesEmailCondition: 'Assignee email',
          EventDoesNotHaveTagCondition: 'Event does not have tag',
          EventHasTagCondition: 'Event has tag',
          EventTaskStatusCondition: 'Event task status',
          InvoiceIsDepositCondition: 'Is deposit invoice',
          InvoiceStatusCondition: 'Invoice status',
          LocationContactsEmailCondition: 'Location contact email',
          LocationContactsPhoneCondition: 'Location contact phone',
          NewPipelineStageNameCondition: 'New pipeline stage name',
          NewlyAddedTagCondition: 'Newly added tag',
          OldPipelineStageNameCondition: 'Old pipeline stage name',
          ProjectDoesNotHaveTagCondition: 'Project does not have tag',
          ProjectHasTagCondition: 'Project has tag',
          ProjectPipelineCondition: 'Project pipeline',
          ProjectPipelineStageNameCondition: 'Pipeline stage name',
          ProjectSiteAddressContactsEmailCondition:
            'Site address contact email',
          ProjectSiteAddressContactsPhoneCondition:
            'Site address contact phone',
          ProposalStatusCondition: 'Proposal status',
          TriggeredDayOfWeekCondition: 'Day of week',
          UserEmailCondition: 'User email',
        },
        boolean: {
          true: 'Yes',
          false: 'No',
        },
        labels: {
          AssigneesPhoneCondition: 'Assignee phone',
          ConnectionApplicationStatusCondition:
            'Connection application status is {{value}}',
          CustomerEmailCondition: 'Customer email',
          CustomerNameCondition: 'Customer name',
          CustomerPhoneCondition: 'Customer phone',
          EventAssigneesEmailCondition: 'Event assignee email',
          EventDoesNotHaveTagCondition: "Event doesn't have tag '{{value}}'",
          EventHasTagCondition: "Event has tag '{{value}}'",
          EventTaskStatusCondition: 'Event task status is {{value}}',
          InvoiceIsDepositCondition_true: 'Invoice is deposit',
          InvoiceIsDepositCondition_false: 'Invoice is not deposit',
          InvoiceStatusCondition: 'Invoice status is {{value}}',
          LocationContactsEmailCondition: 'Location contact email',
          LocationContactsPhoneCondition: 'Location contact phone',
          NewPipelineStageNameCondition: 'New pipeline stage',
          NewlyAddedTagCondition: "Newly added tag is '{{value}}'",
          OldPipelineStageNameCondition: 'Old pipeline stage',
          ProjectDoesNotHaveTagCondition:
            "Project doesn't have tag '{{value}}'",
          ProjectHasTagCondition: "Project has tag '{{value}}'",
          ProjectPipelineCondition: "Pipeline is '{{value}}'",
          ProjectPipelineStageNameCondition: 'Pipeline stage',
          ProjectSiteAddressContactsEmailCondition:
            'Site address contact email',
          ProjectSiteAddressContactsPhoneCondition:
            'Site address contact phone',
          ProposalStatusCondition: 'Proposal status is {{value}}',
          TriggeredDayOfWeekCondition: 'Day of week is {{value}}',
          UserEmailCondition: 'User email',
        },
        operators: {
          EQUALS: 'equals',
          INCLUDES: 'includes',
          NOT_EQUALS: 'does not equal',
        },
        statuses: {
          // ConnectionApplicationStatus
          AUTO_APPROVED: 'Auto approved',
          AWAITING_ASSESSMENT: 'Awaiting assessment',
          COMMISSIONING_ACCEPTED: 'Commissioning accepted',
          COMMISSIONING_SUBMITTED: 'Commissioning submitted',
          COMMISSIONING_UPDATE_REQUIRED: 'Commissioning update required',
          DNO_APPROVED: 'DNO approved',
          DNO_APPROVED_WITH_LIMIT: 'DNO approved with limit',
          ESCALATED_TO_DNO: 'Escalated to DNO',
          EX_MPAN_PROVIDED: 'Ex-MPAN provided',
          FAILED: 'Failed',
          FAILED_INVALID_CUT_OUT_IMAGE: 'Failed: invalid cut out image',
          FAILED_TOO_LARGE: 'Failed: too large',
          FURTHER_INFORMATION_REQUIRED: 'Further information required',
          IMAGE_SCAN_FAILED: 'Image scan failed',
          RE_AUTO_APPROVED: 'Re-auto approved',
          RE_ESCALATED_TO_DNO: 'Re-escalated to DNO',
          SCANNING_CUT_OUT_IMAGE: 'Scanning cut out image',
          UNDER_ASSESSMENT: 'Under assessment',
          // EventTaskStatus
          COMPLETED: 'Completed',
          NOT_STARTED: 'Not started',
          NO_TASKS: 'No tasks',
          PARTIALLY_COMPLETED: 'Partially completed',
          // ReadableInvoiceStatus
          DRAFT: 'Draft',
          PAID: 'Paid',
          PARTIALLY_PAID: 'Partially paid',
          SENT: 'Sent',
          VOID: 'Void',
          // ReadableProposalStatus
          ACCEPTED: 'Accepted',
          ARCHIVED: 'Archived',
          DECLINED: 'Declined',
          DEPOSIT_DUE: 'Deposit due',
          DEPOSIT_PAID: 'Deposit paid',
          EXPIRED: 'Expired',
          INACTIVE: 'Inactive',
          PAYMENT_PENDING: 'Payment pending',
          // Note: SENT is already defined for InvoiceStatus
        },
        dayOfWeek: {
          Monday: 'Monday',
          Tuesday: 'Tuesday',
          Wednesday: 'Wednesday',
          Thursday: 'Thursday',
          Friday: 'Friday',
          Saturday: 'Saturday',
          Sunday: 'Sunday',
        },
      },
      actions: {
        labels: {
          CreateProjectTaskAutomationAction: "Create task '{{taskName}}'",
          EmailNotificationAutomationAction: 'Email to {{recipient}}',
          MoveProjectToPipelineStageAutomationAction:
            "Move to '{{stageName}}' stage",
          RemoveEventTagsAutomationAction: "Remove event tag '{{tagName}}'",
          RemoveEventTagsAutomationAction_noTag: 'Remove event tags',
          RemoveProjectTagsAutomationAction: "Remove project tag '{{tagName}}'",
          RemoveProjectTagsAutomationAction_noTag: 'Remove project tags',
          SmsNotificationAutomationAction: 'SMS to {{recipient}}',
          TagEventAutomationAction: "Add event tag '{{tagName}}'",
          TagProjectAutomationAction: "Add project tag '{{tagName}}'",
          CreateRepeatProjectAutomationAction: 'Create repeat project',
        },
        sendToEmail: {
          CUSTOM_EMAIL_ADDRESS: 'custom email address',
          EVENT_ASSIGNED_USERS_EMAIL_ADDRESS_ARRAY: 'event assignees',
          EVENT_LOCATION_CONTACTS_EMAIL_ADDRESS_ARRAY: 'location contacts',
          PRIMARY_CONTACT_EMAIL_ADDRESS: 'primary contact',
          PROJECT_SITE_ADDRESSES_CONTACTS_EMAIL_ADDRESS_ARRAY: 'site contacts',
          USER_EMAIL_ADDRESS: 'assigned user',
        },
        sendToNumber: {
          CUSTOM_TELEPHONE_NUMBER: 'custom phone number',
          EVENT_ASSIGNED_USERS_TELEPHONE_NUMBER_ARRAY: 'event assignees',
          PRIMARY_CONTACT_TELEPHONE_NUMBER: 'primary contact',
          PROJECT_SITE_ADDRESSES_CONTACTS_TELEPHONE_NUMBER_ARRAY:
            'site contacts',
        },
      },
    },
    pipelineGuards: {
      conditions: {
        PROJECT_HAS_ACCEPTED_PROPOSAL: {
          mustBe: 'Proposal must be accepted',
          mustNotBe: 'Proposal must not be accepted',
          hasBeen: 'Proposal has been accepted',
          hasNotBeen: 'Proposal has not been accepted',
        },
        PROJECT_HAS_ACTIVE_SENT_PROPOSAL: {
          mustBe: 'Proposal must have been sent',
          mustNotBe: 'Proposal must not have been sent',
          hasBeen: 'Proposal has been sent',
          hasNotBeen: 'Proposal has not been sent',
        },
        PROJECT_HAS_PAID_DEPOSIT_INVOICE: {
          mustBe: 'Deposit Invoice must be fully paid',
          mustNotBe: 'Deposit Invoice must not be fully paid',
          hasBeen: 'Deposit Invoice has been fully paid',
          hasNotBeen: 'Deposit Invoice has not been fully paid',
        },
        PROJECT_ACCEPTED_VALUE_IS_FULLY_INVOICED: {
          mustBe: 'Accepted value must be fully invoiced',
          mustNotBe: 'Accepted value must not be fully invoiced',
          hasBeen: 'Accepted value has been fully invoiced',
          hasNotBeen: 'Accepted value has not been fully invoiced',
        },
        PROJECT_ACCEPTED_VALUE_IS_FULLY_PAID: {
          mustBe: 'Accepted value must be fully paid',
          mustNotBe: 'Accepted value must not be fully paid',
          hasBeen: 'Accepted value has been fully paid',
          hasNotBeen: 'Accepted value has not been fully paid',
        },
        PROJECT_HAS_EXPIRED_PROPOSAL: {
          mustBe: 'Proposal must have expired',
          mustNotBe: 'Proposal must not have expired',
          hasBeen: 'Proposal has expired',
          hasNotBeen: 'Proposal has not expired',
        },
        PROJECT_HAS_DECLINED_PROPOSAL: {
          mustBe: 'Proposal must have been declined',
          mustNotBe: 'Proposal must not have been declined',
          hasBeen: 'Proposal has been declined',
          hasNotBeen: 'Proposal has not been declined',
        },
        PROJECT_HAS_TAG: {
          mustBe: 'Project must have tag "{{tagName}}"',
          mustBe_blank: 'Project must have tag',
          mustNotBe: 'Project must not have tag "{{tagName}}"',
          mustNotBe_blank: 'Project must not have tag',
          hasBeen: 'Project has tag "{{tagName}}"',
          hasBeen_blank: 'Project has tag',
          hasNotBeen: 'Project does not have tag "{{tagName}}"',
          hasNotBeen_blank: 'Project does not have tag',
        },
        CUSTOM_FIELD_CONDITION: {
          entry: 'Custom field value meets condition',
          selectedLabel: 'Custom field',
          field: {
            label: 'Custom field',
            placeholder: 'Select a Custom Field…',
          },
          operator: {
            label: 'Condition',
            placeholder: 'Select a condition…',
            equals: 'equals',
            notEquals: 'does not equal',
            isSet: 'is set',
            isNotSet: 'is not set',
            allChildrenAreSet: 'every field is set',
            allChildrenAreNotSet: 'no field is set',
            anyChildIsSet: 'at least one field is set',
          },
          comparisonValue: {
            label: 'Value',
            placeholder: 'Enter a value…',
          },
          summary: {
            equals: '"{{field}}" must equal {{value}}',
            notEquals: '"{{field}}" must not equal {{value}}',
            isSet: '"{{field}}" must be set',
            isNotSet: '"{{field}}" must not be set',
            allChildrenAreSet: 'Every field in "{{field}}" must be set',
            allChildrenAreNotSet: 'No field in "{{field}}" may be set',
            anyChildIsSet: 'At least one field in "{{field}}" must be set',
          },
        },
      },
    },
    dashboard: {
      header: 'Dashboard',
      greeting: "Hi {{firstName}}, here's how {{companyName}} is doing",
      trends: 'Trends',
      metric: {
        up: 'up',
        down: 'down',
        from: 'from',
        overdue: 'Overdue',
        allClear: 'All clear',
      },
      dateRange: {
        label: 'Choose your time period',
        thisMonth: 'This month',
        lastMonth: 'Last month',
        last3Months: 'Last 3 months',
        last6Months: 'Last 6 months',
        last12Months: 'Last 12 months',
        allTime: 'All time',
        previousThisMonth: 'in same period last month (partial)',
        previousLastMonth: 'in previous month',
        previous3Months: 'in previous 3 months',
        previous6Months: 'in previous 6 months',
        previous12Months: 'in previous 12 months',
      },
      summary: {
        title: 'Last 30 Days',
        comparison: 'vs previous 30 days',
        comparisonPrefix: 'vs previous period',
        revenueSecured: 'Revenue Secured',
        revenueSecuredTooltip:
          'Total value of proposals accepted by customers in this period (exc tax). Includes change proposals.',
        revenueSecuredEmpty: 'Send and accept proposals to see revenue here',
        revenueReceived: 'Revenue Received',
        revenueReceivedTooltip:
          'Total value of payments received in this period (exc tax)',
        revenueReceivedEmpty: 'Record payments to track revenue received',
        overdueTitle: 'Overdue',
        overdueTooltip: 'Total value of invoices past their due date (exc tax)',
      },
      proposals: {
        title: 'Proposals',
        titleTooltip: 'Excluding change proposals',
        sent: 'Sent',
        sentTooltip: 'Total value of proposals sent to customers (exc tax)',
        accepted: 'Accepted',
        acceptedTooltip:
          'Total value of proposals accepted by customers (exc tax)',
        declined: 'Declined',
        declinedTooltip:
          'Total value of proposals declined by customers (exc tax)',
        acceptanceRate: 'Acceptance Rate',
        xAxisTitle: 'Proposal sent month',
        empty: 'No proposal data available',
      },
      invoices: {
        title: 'Invoices',
        sent: 'Sent',
        sentTooltip: 'Total value of invoices sent to customers (exc tax)',
        paid: 'Paid',
        paidTooltip: 'Total value of invoices paid by customers (exc tax)',
        overdue: 'Overdue',
        overdueTooltip: 'Total value of invoices past their due date (exc tax)',
        paidRate: 'Paid Rate',
        xAxisTitle: 'Invoice sent month',
        empty: 'No invoice data available',
      },
      errors: {
        summaryError: 'Unable to load summary data. Please try again later.',
        proposalsError: 'Unable to load proposal data. Please try again later.',
        invoicesError: 'Unable to load invoice data. Please try again later.',
        breakdownError:
          'Unable to load breakdown data. Please try again later.',
      },
      quickStats: {
        proposalsSent: 'Proposals Sent',
        proposalsSentTooltip: 'Excludes change proposals',
        pendingProposals: 'Pending Proposals',
        pendingProposalsTooltip: 'Excludes change proposals',
        invoicesSent: 'Invoices Sent',
        awaitingPayment: 'Awaiting Payment',
        collectionRate: 'Collection Rate',
        ofInvoicedValue: 'of invoiced value',
        avgDealSize: 'Avg Project Value',
        avgDealSizeTooltip:
          'Based on original proposals only, excludes change proposals',
        invoice: 'Invoice',
      },
      breakdown: {
        proposalStatus: 'Proposal Status',
        proposalStatusTooltip: 'Excludes change proposals',
        invoiceStatus: 'Invoice Status',
        pending: 'Pending',
        accepted: 'Accepted',
        declined: 'Declined',
        paid: 'Paid',
        awaiting: 'Awaiting Payment',
        overdue: 'Overdue',
        noProposals: 'No proposal data available for this period',
        noInvoices: 'No invoice data available for this period',
      },
    },
  },
  'en-US': {
    navigation: {
      persistent: {
        deals: {},
      },
    },
    pages: {
      deals: {
        ctas: {
          createDeal: {},
        },
      },
      deal: {},
    },
  },
  'af-ZA': {
    navigation: {
      persistent: {
        dashboard: {
          label: 'Dashboard',
        },
        invoices: {
          label: 'Invoices',
        },
        deals: {
          label: 'Projects',
        },
        customers: {
          label: 'Kliënte',
        },
        schedule: {
          label: 'Schedule',
        },
        dispatch: {
          label: 'Dispatch',
        },
        tasks: {
          label: 'Take',
        },
        items: {
          label: 'Lynitems',
        },
        materials: {
          label: 'Materiaal',
        },
        suppliers: {
          label: 'Verskaffers',
        },
        automations: {
          label: 'Outomatiserings',
        },
        analytics: {
          label: 'Optrede',
        },
        servicePlans: {
          label: 'Service Plans',
        },
      },
      account: {
        profile: {
          label: 'Profiel',
        },
        myCompany: {
          label: 'My maatskappy',
        },
        users: {
          label: 'Gebruikers',
        },
        manageSubscription: {
          label: 'Bestuur intekening',
        },
        tags: {
          label: 'Tags',
        },
        csvUploads: {
          label: 'Import data',
        },
        connections: {
          label: 'Verbindings',
        },
        logOut: {
          label: 'Teken uit',
        },
      },
    },
    pages: {
      dashboard: {
        title: 'Dashboard',
      },
      invoices: {
        title: 'Invoices',
      },
      deals: {
        title: 'Projects',
        ctas: {
          createDeal: {
            label: 'New Project',
          },
        },
      },
      customers: {
        title: 'Customers',
        heading: 'Customers',
        ctas: {
          createCustomer: {
            label: 'Create new',
          },
        },
      },
      schedule: {
        title: 'Schedule',
        heading: 'Schedule',
      },
      tasks: {
        title: 'Tasks',
        heading: 'Tasks',
        ctas: {
          createTask: {
            label: 'Create new',
          },
        },
      },
      items: {
        title: 'Items',
      },
      materials: {
        title: 'Materials',
      },
      suppliers: {
        title: 'Suppliers',
      },
      automations: {
        title: 'Automations',
      },
      analytics: {
        title: 'Analytics',
      },
    },
  },
};

const defaultNamespace = 'translations';

// oxlint-disable-next-line typescript-eslint(no-floating-promises) -- FIXME: pre-existing error: Promises must be awaited.
i18n.use(initReactI18next).init({
  resources: Object.fromEntries(
    Object.entries(translations).map(([key, value]) => [
      key,
      {
        [defaultNamespace]: value,
      },
    ])
  ),
  supportedLngs: supportedLanguages,
  lng: navigator.language,
  defaultNS: defaultNamespace,
  fallbackLng: {
    default: [defaultLanguage],
    en: ['en-GB'],
    // TODO: Uncomment this once translations have been approved
    // af: ['af-ZA'],
    // 'af-ZA': ['en-ZA'],
    // 'en-ZA': ['en-GB'],
  },
  interpolation: {
    escapeValue: false,
  },
});

export const useTranslation = (keyPrefix?: string) => {
  const { t } = useVendorTranslation(defaultNamespace, {
    keyPrefix,
  });
  return t;
};
